import React, { FormEvent, useEffect, useState } from 'react'
import { emptyFormDTO, FormDTO, FormFieldDTO, fromModel } from '../../modules/forms/models/FormDTO'
import { getFormContainer } from '../../container/form-module'
import { FormService } from '../../modules/forms/services/FormService'
import { FORM_SERVICE_KEY } from '../../modules/forms'
import {
  Box,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import { EditorFields } from './EditorFields'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import { navigate } from '@reach/router'
import { ROUTE_FORM_GENERATOR } from '../../routes/routes-constants'
import { isSpecialType } from '../../common/utils/enums'
import { Alert } from '@material-ui/lab'
import { useTranslation } from 'react-i18next'
import style from './Editor.module.css'
import { CronItemMockup } from 'components/form-card/CronItemMockup'
import { Cron, cronString } from '../../modules/forms/enums/Cron'
import { PreviewForm } from './PreviewForm'
import { FormCategory, CATEGORY_OPTIONS, getCategoryLabel } from '../../common/enums/FormCategories'
import { PageLayout } from 'pages/layout'
import { IStatusService } from '../../common/status/StatusService'
import { getAppContainer, STATUS_SERVICE_KEY } from '../../container/app'

enum FormFieldsError {
  NO_FIELDS = 'formShouldHaveAtLeastAField',
  ORDER = 'formFieldsErrorOrder',
  NO_OPTIONS = 'formFieldsErrorNoOptions',
  CONSECUTIVE = 'formFieldsErrorConsecutive',
}

type EditorProps = {
  id?: string
}

// Shared so title, description and every question field look the same.
const filledField = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '999px',
    backgroundColor: 'var(--gray-soft, #e8e7ec)',
    fontSize: '14px',
  },
  '& .MuiOutlinedInput-root.MuiInputBase-multiline': { borderRadius: '20px' },
  '& fieldset': { border: 'none' },
}

const fieldLabel = { fontSize: '14px', color: 'var(--gray-medium)', width: 120, flexShrink: 0 }

const formContainer = getFormContainer()
const formService = formContainer.get<FormService>(FORM_SERVICE_KEY)

export function Editor(props: EditorProps) {
  const { t } = useTranslation()
  const cronTypes = cronString()
  const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)

  const [form, setForm] = useState<FormDTO>(emptyFormDTO())
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isRecurrence] = useState<boolean>(false)
  const [cron, setCron] = useState<string>(cronTypes[Cron.Empty])
  const [preview, setPreview] = useState<boolean>(false)
  const [dataPreview, setDataPreview] = useState<FormDTO>(emptyFormDTO())
  // On create, the first save inserts and later ones update the same form.
  const [savedId, setSavedId] = useState<string | undefined>(props.id)
  const [saving, setSaving] = useState<boolean>(false)

  const handleChangeRecurrence = (s: string) => setCron(s)

  useEffect(() => {
    if (!props?.id) {
      return
    }
    formService.getByID(props.id).subscribe((res) => {
      if (res) {
        setForm(fromModel(res))
      }
    })
  }, [])

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(Object.assign({ ...form }, { [e.target.name]: e.target.value }))

  const handleCategoryChange = (e: any) => {
    setForm(Object.assign({ ...form }, { category: e.target.value }))
  }

  const changeFormFields = (formFields: FormFieldDTO[]) => (form.formFields = formFields)

  // Payload for the backend, with the fields already bound to the form.
  const buildForm = (): FormDTO => {
    const newFormFields: FormFieldDTO[] = form.formFields.map((ff) =>
      Object.assign({ ...ff }, { formID: savedId ?? form.id, order: ff.order })
    )
    return Object.assign(
      { ...form },
      {
        id: savedId ?? form.id,
        formFields: newFormFields,
        repeating: cron,
        category: form.category || FormCategory.OTHER,
      }
    )
  }

  const persist = (leave: boolean) => {
    if (!form.title.trim()) {
      setErrorMessage('formTitleRequired')
      return
    }
    if (!validateForm()) {
      return
    }

    const newForm = buildForm()
    setSaving(true)
    const peticion = savedId ? formService.update(newForm) : formService.add(newForm)

    peticion.subscribe((res) => {
      setSaving(false)
      if (leave) {
        goBack()
        return
      }
      // Reload with the response to keep backend-assigned ids: without them
      // the next save would duplicate the questions.
      if (res) {
        setSavedId(res.id)
        setForm(fromModel(res))
      }
      statusService.sendStatus({ variant: 'success' })
    })
  }

  const saveForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    persist(true)
  }

  const validateForm = () => {
    const orders = form.formFields.map((f) => f.order)
    const hasDuplicateOrders = orders.some((order, index) => orders.indexOf(order) !== index)

    if (hasDuplicateOrders) {
      setErrorMessage(FormFieldsError.ORDER)
      return false
    }

    if (form.formFields.length > 0) {
      const sortedOrders = [...orders].sort((a, b) => a - b)
      const hasGaps = sortedOrders.some((order, index) => order !== index + 1)

      if (hasGaps) {
        setErrorMessage('formFieldsErrorConsecutive')
        return false
      }
    }

    // Validate special fields with no options.
    if (
      form.formFields.some(
        (f) => isSpecialType(f.type) && (!f.optionValues || f.optionValues?.length === 0)
      )
    ) {
      setErrorMessage(FormFieldsError.NO_OPTIONS)
      return false
    }

    if (form.formFields.length === 0) {
      setErrorMessage(FormFieldsError.NO_FIELDS)
      return false
    }

    setErrorMessage('')
    return true
  }

  const goBack = () => navigate(ROUTE_FORM_GENERATOR)

  const showPreview = () => {
    const newFormFields: FormFieldDTO[] = []
    form.formFields.forEach((ff) => {
      newFormFields.push(Object.assign({ ...ff }, { formID: form.id, order: ff.order }))
    })
    const newForm = Object.assign(
      { ...form },
      {
        formFields: newFormFields,
        repeating: cron,
        category: form.category || FormCategory.OTHER,
      }
    )
    setDataPreview(newForm)
    setPreview(true)
  }

  return (
    <PageLayout>
      {preview && (
        <>
          <PreviewForm
            title={dataPreview.title}
            description={dataPreview.description}
            category={dataPreview.category}
            formFields={dataPreview.formFields}
            repeating={dataPreview.repeating}
            id=""
          />

          <Box mb={2} mt={2}>
            <strong>{t('category')}:</strong> {t(getCategoryLabel(dataPreview.category))}
          </Box>

          <Box display="flex" justifyContent="flex-end">
            <AppButton
              theme={ButtonTheme.NewPrimary}
              type="button"
              label={t('closePreview')}
              handler={() => {
                setPreview(false)
              }}
            />
          </Box>
        </>
      )}
      {!preview && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
          <Box sx={{ flexShrink: 0 }} mb={3}>
            <Typography sx={{ fontSize: '22px', fontWeight: 600, color: 'var(--gray-medium)' }}>
              {t(props.id ? 'editQuestionnaire' : 'createNewQuestionnaire')}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'var(--gray-text)' }}>
              {t('questionnaireEditorSubtitle')}
            </Typography>
          </Box>

          <form
            className={style.form}
            onSubmit={(e) => saveForm(e)}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}
          >
            {/* Right padding: the focus ring and the required-field warning
                were clipped against the scrolling container. */}
            <Box sx={{ flex: 1, overflow: 'auto', pr: 2 }}>
              <Box mb={2} display="flex" alignItems="center" gap={2}>
                <Typography sx={fieldLabel}>{t('title')}</Typography>
                <TextField
                  name="title"
                  value={form.title}
                  onChange={handleInput}
                  placeholder={t('insertTitle')}
                  size="small"
                  required
                  sx={{ ...filledField, flex: 1 }}
                />
              </Box>

              <Box mb={3} display="flex" alignItems="flex-start" gap={2}>
                <Typography sx={{ ...fieldLabel, mt: 1 }}>{t('description')}</Typography>
                <TextField
                  name="description"
                  value={form.description}
                  onChange={handleInput}
                  placeholder={t('insertDescription')}
                  multiline
                  rows={3}
                  required
                  sx={{ ...filledField, flex: 1 }}
                />
              </Box>

              {/* Not in the mockup, but the form list still filters by it. */}
              <Box mb={3} display="flex" alignItems="center" gap={2}>
                <Typography sx={fieldLabel}>{t('category')}</Typography>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>{t('selectCategory')}</InputLabel>
                  <Select
                    value={form.category || FormCategory.OTHER}
                    onChange={handleCategoryChange}
                    label={t('selectCategory')}
                    required
                    sx={{ borderRadius: '999px', fontSize: '14px' }}
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.label)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {isRecurrence && (
                <Box mb={3}>
                  <CronItemMockup handleChange={handleChangeRecurrence} />
                </Box>
              )}

              <Box mb={3} className={style.box}>
                <EditorFields
                  formFields={form.formFields.sort((a, b) => a.order - b.order)}
                  handlerChange={changeFormFields}
                />
              </Box>

              {errorMessage && (
                <Box mb={3}>
                  <Alert severity="warning" key="errorMessage" id="errorMessage">
                    {t(errorMessage)}
                  </Alert>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 3,
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                {/* Saves without leaving, to keep adding questions. */}
                <Button
                  type="button"
                  disabled={saving}
                  startIcon={<SaveOutlinedIcon sx={{ fontSize: 18 }} />}
                  onClick={() => persist(false)}
                  sx={{ textTransform: 'none', color: 'var(--gray-text)', fontSize: '14px' }}
                >
                  {saving ? t('saving') : t('saveChanges')}
                </Button>
                <Button
                  type="button"
                  startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                  onClick={showPreview}
                  sx={{ textTransform: 'none', color: 'var(--gray-text)', fontSize: '14px' }}
                >
                  {t('previewForm')}
                </Button>
              </Box>

              <Box display="flex" gap={1.5}>
                <Button
                  type="button"
                  onClick={goBack}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '999px',
                    border: '1px solid var(--gray-border)',
                    color: 'var(--gray-text)',
                    px: 3,
                  }}
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  sx={{
                    textTransform: 'none',
                    borderRadius: '999px',
                    px: 3,
                    backgroundColor: 'var(--green-primary)',
                    '&:hover': { backgroundColor: 'var(--green-dark)' },
                  }}
                >
                  {t(savedId ? 'save' : 'create')}
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      )}
    </PageLayout>
  )
}
